import { isDefined, isNotDefined } from '@sgme/fp';
import classnames from 'classnames';
import { OnKeyDownArg } from 'components/common-form/builder/form/node/field/Grid/Keys';
import {
  connectSpecificOrBlackoutDate,
  SpecificOfBlackoutDateProps,
} from 'components/common-form/builder/form/node/field/SpecificOrBlackoutDateField.connect';
import { BatchNavigationContext } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { useBlockOrFieldReadOnly } from 'components/common-form/hooks/useBlockOrFieldReadOnly';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { useFieldValidationError } from 'components/common-form/hooks/useFieldValidationError';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { type DatePickerSize } from 'components/shared/DatePicker/DatePicker';
import { MonoDatePicker } from 'components/shared/DatePicker/MonoDatePicker';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { DispatchContext } from 'contexts/dispatch.context';
import { useSPIntl } from 'hooks/useIntl';
import { usePasteSingleDate } from 'hooks/usePasteSingleDate';
import { DateFieldData, DateFieldMetadata } from 'models/forms/fields';
import {
  SpecificOrBlackoutDateFieldData,
  SpecificOrBlackoutDateFieldMetadata,
} from 'models/forms/fields/SpecificOrBlackoutDateFieldMetadata';
import { FC, useCallback, useContext } from 'react';
import { formUnderlyingsDatesRequestedThunk } from 'state/underlyingsDates/underlyingsDates.thunks';
import type { WorkspaceType } from 'state/workspaces/model';
import { getDateOnlyFromIsoString } from 'utils/date';

const SpecificOrBlackoutDate: FC<SpecificOfBlackoutDateProps> = ({
  node: { id },
  className,
  workspaceType,
  tabId,
  tabIndex,
  specificDates,
  blackoutDates,
}) => {
  const translationKey = useFieldTranslationKey(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const dispatch = useContext(DispatchContext)!;
  const { onlySpecificDates } = useFieldMetadata<SpecificOrBlackoutDateFieldMetadata>(id)!;
  const isSpecificDates = onlySpecificDates && isDefined(specificDates);
  const isBlackoutDates = !onlySpecificDates && isDefined(blackoutDates);
  if (onlySpecificDates && !isDefined(specificDates)) {
    dispatch(formUnderlyingsDatesRequestedThunk(tabId, onlySpecificDates, workspaceType));
  }
  if (!onlySpecificDates && !isDefined(blackoutDates)) {
    dispatch(formUnderlyingsDatesRequestedThunk(tabId, onlySpecificDates, workspaceType));
  }

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${translationKey}` as any}
      workspaceType={workspaceType}
      className={patchedFieldClassName}
    >
      <FieldDatePicker
        id={id}
        tabId={tabId}
        className={className}
        tabIndex={tabIndex}
        workspaceType={workspaceType}
        blackoutDates={isBlackoutDates ? blackoutDates : undefined}
        specificDates={isSpecificDates ? specificDates : undefined}
      />
    </BlockOrientationFormGroup>
  );
};
SpecificOrBlackoutDate.displayName = 'SpecificOrBlackoutDateField';

type FieldDatePickerProps = {
  id: string;
  size?: DatePickerSize;
  className?: string;
  tabId: string;
  hasError?: boolean;
  tabIndex?: number;
  workspaceType: WorkspaceType;
  blackoutDates?: string[];
  specificDates?: string[];
};
const FieldDatePicker: FC<FieldDatePickerProps> = ({
  id,
  size,
  className,
  tabId,
  hasError,
  tabIndex,
  workspaceType,
  specificDates,
  blackoutDates,
}) => {
  const { formatDisplayedDate } = useSPIntl();
  const isFieldInvalid = useFieldValidationError(id)?.severity !== undefined;
  const isReadonly = useBlockOrFieldReadOnly(id);
  const { allowWeekend, isClearable, startDate, endDate, isReadOnly } = useFieldMetadata<DateFieldMetadata>(id)!;
  const [value, setValue] = useData<SpecificOrBlackoutDateFieldData | DateFieldData>(id);
  const { isCellInactive, isFocused, lostInputBeforeEditing, onEditingKeyDown } = useContext(BatchNavigationContext);

  const onChange = useCallback(
    (newValue: Nullable<string>) => {
      if (newValue !== value) {
        setValue(newValue);
      }
    },
    [value, setValue],
  );

  const formattedDate = getDateOnlyFromIsoString(value);
  const onPaste = usePasteSingleDate(setValue);
  const readOnlyFormattedValue = isDefined(value) ? formatDisplayedDate(value) : '';

  const getSpecificDates = () => {
    if (!specificDates?.some(date => date === value) && isDefined(value)) {
      return specificDates?.concat([value]);
    }
    return specificDates;
  };

  const handleKeyDown = useCallback(
    ({ e, commit }: OnKeyDownArg) => {
      if (onEditingKeyDown) {
        onEditingKeyDown(e);
        switch (e.key) {
          case 'Enter':
          case 'Tab':
            commit();
            break;
          default:
            break;
        }
      }
    },
    [onEditingKeyDown],
  );

  if (isCellInactive || isReadOnly || (isNotDefined(specificDates) && isNotDefined(blackoutDates))) {
    return (
      <input
        className={'form-control pe-none'}
        data-e2e={id + '-input'}
        id={id}
        name={id}
        readOnly={isReadOnly}
        type="text"
        value={readOnlyFormattedValue}
        tabIndex={tabIndex}
        disabled={isReadOnly}
      />
    );
  }

  return (
    <MonoDatePicker
      className={classnames(className)}
      size={size}
      value={formattedDate as Nullable<string>}
      id={id}
      onChange={onChange}
      onPaste={onPaste}
      disabled={isReadonly}
      allowWeekend={allowWeekend}
      isClearable={isClearable ?? false}
      minDate={startDate}
      maxDate={endDate}
      hasError={isFieldInvalid || hasError === true}
      availableDates={getSpecificDates()}
      blackoutDates={blackoutDates}
      tabId={tabId}
      tabIndex={tabIndex}
      isFocused={isFocused}
      onKeyDown={handleKeyDown}
      workspaceType={workspaceType}
      inputValue={lostInputBeforeEditing}
    />
  );
};

export const SpecificOrBlackoutDateField = connectSpecificOrBlackoutDate(SpecificOrBlackoutDate);
